import { captureException } from "scripts/logging/error-report";
import { PropertyPopUp } from "./PropertyPopUp";
import { createPortal } from "react-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { useEffect, useRef, useState } from "react";
import {
  isEmpty,
  parseCompositeHarid,
  resolveListingPrice,
} from "utils/helpers";
import { PropertyMlsStatus } from "./PropertyCard/MLSStatus";
import { Link, useLocation } from "react-router-dom";
import { fetchWrapper } from "services/api-client/fetchWrapper";
import { useBookmarkContext } from "services/BookmarkContext";
import { MLSDetails } from "./PropertyCard/PropertyCard.def";
import { DEFAULT_FILTERS, extractFilterValuesAndAppendInQueryString, IFilters, processUIFilters, toUIFilters } from "./Filters/Filters.def";
import { Filter, STATUSES_THAT_CLEAR_FOR } from "./Filters/Filters";
import { useUserContext } from "services/UserContext";
import { ISearchListingsArtifact } from "types/artifacts/search-listings";
import { apiBaseUrl, PLACEHOLDER_IMAGE } from "utils/constants";
import { AiSummaryDrawer } from "./AiSummaryDrawer/AiSummaryDrawer";
import { useQueryFlags } from "scripts/hooks/useQueryFlags";
import { ResultsCountAndSeeMore } from "./ResultsCountAndSeeMore";
import { AgentInfoCard } from "./AgentInfoCard";
import { getCommunityDetailsById, getDistrictSchoolDetailsById, getNeighborhoodDetailsById, getSchoolsDetailsByIds } from "services";
import { isProduction, websiteHost, websiteProductionHost } from "scripts/application/setting";
import { trackEvent } from "services/api-client/matomoTracker";
import { MessageActionBar } from "./MessageActionBar";
import { IMessage } from "types/services";
import { eSearchType } from "operations/search-filter-operation";
export interface IMlsSearchCardCarouselProps {
  artifact: ISearchListingsArtifact;
  onFiltersNewMessage?: (filters: IFilters, type?: eSearchType) => void;
  hideFiltersAndAiSummary?: boolean;
  /** Suppress the "Load more properties" tile. The tile dispatches a
   * `search_listings` next-page operation, which is only meaningful for a
   * search_listings artifact; on any other artifact it would silently swap the
   * user's results for a plain listings search. */
  hideLoadMore?: boolean;
  message?: IMessage;
}

export function MlsSearchCard(props: { details: MLSDetails }) {
  const { isContentNotVisible, cId, embed, source } = useQueryFlags();
  const { search } = useLocation();
  const { details } = props;
  const [showPopUp, setShowPopUp] = useState(false);
  const { bookmark, setBookmark } = useBookmarkContext();
  const bookmarked = bookmark?.mlsnums.includes(details.mlsnum) ? 1 : 0;
  const { user } = useUserContext();
  // const isLoggedin = (user?.role_id ?? 0) > 0;
  const isLoggedin = user?.role !== "guest";
  // True when running inside a property popup iframe — chat links must open in a new tab
  const isListingDetail = source === "listing-detail";

  // When embedded or inside a listing-detail popup, drop context-only flags so
  // the new tab loads the full AI Mode UI. `cid` is kept (identifies the agent).
  const chatLinkSearch = (() => {
    if (!embed && !isListingDetail) return search;
    const params = new URLSearchParams(search);
    ["embed", "source", "hide_user", "hide_header", "hide_footer", "hide_share", "timeline"].forEach(
      (key) => params.delete(key)
    );
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  })();

  // Townhouse/Condo - Condominium · 1 · 1 Full · 1 · 696 · 169.54 · 1969
  // Townhouse/Condo - Condominium · 1 bed · 1 Full baths · 1 story · 696 Building Sqft · $170/Sqft. · 1969 year built
  const joinWithBullet = (...values: (string | undefined)[]) =>
    values.filter(v => v && v.trim()).join(' · ');

  const { property_type, beds, bath, stories, sqft, yearbuilt, pricesqft } = details;

  const propertyDetails = joinWithBullet(
    property_type,
    beds ? `${beds} bed` : undefined,
    bath ? `${bath} baths` : undefined,
    stories ? `${stories} story` : undefined,
    sqft ? `${sqft} Building Sqft` : undefined,
    pricesqft ? `$${pricesqft}/Sqft.` : undefined,
    yearbuilt ? `${yearbuilt} year built` : undefined
  );
  const handleCardClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isContentNotVisible) {
      window.open(details?.share_url, "_blank", "noopener,noreferrer");
    } else {
      setShowPopUp(true);
    }
  };
  const handleBookmark = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      !bookmarked
        ? await fetchWrapper.post(
          `/api/v1/chatbot/bookmark/${details?.mlsnum}`,
          {}
        )
        : await fetchWrapper._delete(
          `/api/v1/chatbot/bookmark/${details?.mlsnum}`
        );
      const bookmarks = bookmarked
        ? bookmark?.mlsnums.filter((item) => item !== details.mlsnum)
        : [...(bookmark?.mlsnums ?? []), details.mlsnum ?? ""];
      setBookmark({ mlsnums: bookmarks ?? [] });
      const event = bookmarked ? "Remove Bookmark" : "Bookmark";
      trackEvent(`AI Mode / Listing Search ${event} / ${details?.harid ?? '0'}`);
    } catch (e) {
      console.error(e);
      captureException(e);
    }
  };

  const isBrandedApp = !!(user.role === "consumer" && user?.connected_agent);
  // Status possible values just for tracking purpose
  // Status	ListStatus
  // Incomplete	INCOM
  // Coming Soon	CS
  // Active	A
  // Active	AL
  // Under Contract - P	P
  // Under Contract - OP	OP
  // Under Contract - PS	PS
  // Sold	CLOSD
  // Rented	LEASED
  // Expired	EXP
  // Withdrawn	WITH
  // Terminated	TERM
  const isChatButtonHidden = ["CLOSD", "LEASED", "EXP", "WITH", "TERM"].includes(details?.status?.short_text ?? "");
  const agentProfileUrl = details.agent?.url || "";
  return (
    <div>
      <div
        className="cardv2 cardv2--portrait cardv2--property mb-0 h-100"
        onClick={handleCardClick}
      >
        <div className="cardv2--portrait__header">
          <div className="cardv2--portrait__header_agent">
            {(!isBrandedApp && !cId) ? (
              <AgentInfoCard
                agentProfileUrl={agentProfileUrl?.startsWith("https://www.har.com") ? agentProfileUrl : `https://www.har.com${agentProfileUrl}`}
                brokerProfileUrl={details?.broker_url || ''}
                name={details.agent?.name}
                photo={details.agent?.photo}
                officeName={details.broker}
                isTextTruncate
              />
            ) : <div style={{ height: '40px' }} />
            }
          </div>
          {isContentNotVisible ? null : (
            <div className="cardv2--portrait__header_controls">
              {details?.status.short_text === "A" && isLoggedin && (
                <a
                  onClick={handleBookmark}
                  className={`btn btn--icon--borderless btn--icon cmd--fav btn--icon--${bookmarked ? "favorited" : "favorite"
                    } mr-3`}
                  title=""
                  style={{ width: "auto" }}
                />
              )}
            </div>
          )}
        </div>
        <div className="cardv2--portrait__img position-relative">
          {details?.photos_count != null && Number(details.photos_count) > 0 && (
            <a className="btn btn--ordinary openGallery d-flex align-items-center px-2" data-media-type="photo-Tab"
              style={{ position: "absolute", bottom: "10px", left: "10px", height: "32px" }}
              onClick={(e) => e.stopPropagation()}
              href={details?.harid ? `${websiteProductionHost}/home-gallery/${parseCompositeHarid(details.harid)}` : details?.share_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img className="mr-2 d-md-block d-none" style={{ width: "22px" }} src="https://content.harstatic.com/media/icons/a_all/photos_icon_black.svg" alt="gallery-icon" />
              <span>{details?.photos_count || 0} photos</span>
            </a>
          )}
          <img
            src={!isEmpty(details.photo) ? details.photo : PLACEHOLDER_IMAGE}
            alt={details.address.street_address}
            onError={(e) => {
              e.currentTarget.onerror = null; // Prevent infinite loop if placeholder also fails
              e.currentTarget.src = PLACEHOLDER_IMAGE;
            }}
            className="w-100 h-100"
          />
          {isContentNotVisible || isChatButtonHidden ? null : (
            <Link
              to={`/listing/${details.harid}${chatLinkSearch}`}
              target={embed || isListingDetail ? "_blank" : undefined}
              rel={embed || isListingDetail ? "noopener noreferrer" : undefined}
              onClick={(e) => { e.stopPropagation(); trackEvent(`AI Mode / Listing Search Card Chat / ${details.harid ?? '0'}`); }}
              className="btn btn--primary btn--small position-absolute"
              style={{ bottom: 7.5, right: 7.5 }}
            >
              <img src="https://content.harstatic.com/media/icons/a_all/chat_ai_icon2.svg" className="ai-mode-white" alt="AI Mode Icon" />
              Chat
            </Link>
          )}
        </div>
        <div className="cardv2--portrait__body">
          <div className="cardv2--portrait__body_flexrow  flex-wrap pb-0">
            <div className="cardv2--portrait__body_price flex-grow-1 pb-0">
              {resolveListingPrice(details.sold_price_display, details.price).value}
            </div>
            <div className="cardv2--portrait__body_status flex-grow-0 pb-1">
              <PropertyMlsStatus mls_status_text={details.status.short_text} />
            </div>
          </div>
          <div className="cardv2--portrait__body_flexrow pb-0">
            <div className="cardv2--portrait__body_address font-weight-normal">
              {details.address.street_address}
              <br />
              {details.address.city}, {details.address.zip_code}
            </div>
          </div>
          <div className="cardv2--description">{propertyDetails}</div>
        </div>

      </div>

      {
        showPopUp &&
        createPortal(
          <PropertyPopUp
            harid={details.harid}
            id={details.id}
            statusShort={details.status.short_text}
            onClose={() => setShowPopUp(false)}
          />,
          document.body
        )
      }
    </div>
  );
}

export namespace MlsSearchCard {
  export function Carousel(props: IMlsSearchCardCarouselProps) {
    const { artifact, onFiltersNewMessage, hideFiltersAndAiSummary = false, hideLoadMore = false, message } = props;
    const { isContentNotVisible, readOnly } = useQueryFlags();
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isAiSummaryDrawerOpen, setIsAiSummaryDrawerOpen] = useState(false);
    const [aiSummary, setAiSummary] = useState<{
      data: any;
      error: string;
      loading: boolean;
    }>({
      data: null,
      error: "",
      loading: true,
    });
    const [filters, setFilters] = useState<IFilters>(DEFAULT_FILTERS);
    const properties = artifact?.response?.properties || [];
    const stop = artifact?.response?.stop || 0;
    const totalNumberOfProperties = artifact?.response?.total_number_of_properties || 0;
    const propertiesCount = properties?.length;
    const processedFiltersRef = useRef<string | null>(null);

    const processFilters = async (shouldAgainProcessFilters: boolean = false) => {
      if (!artifact.filters) {
        processedFiltersRef.current = null;
        setFilters?.({});
        return;
      }

      // Skip if we already processed these exact filters
      const filtersKey = JSON.stringify(artifact.filters);
      if (!shouldAgainProcessFilters && processedFiltersRef.current === filtersKey) return;
      processedFiltersRef.current = filtersKey;

      //const artifactFilters = artifact.filters;
      //const filters = { ...artifactFilters } as any;
      const transformedFilters = await processUIFilters(artifact.filters);

      //const transformedFilters = transformApiFilters(filters);
      // console.log({ transformedFilters, filters });
      if (!shouldAgainProcessFilters) {
        setFilters?.(transformedFilters);
      }

      return transformedFilters;
    };

    useEffect(() => {
      if (hideFiltersAndAiSummary) return;
      processFilters().catch((err) => {
        captureException(err);
        console.error(err);
      });
    }, [artifact.filters, hideFiltersAndAiSummary]);

    const getAiSummaryData = async () => {
      try {
        setAiSummary({ data: null, error: "", loading: true });
        const newSelectedFilters = await processFilters(true);
        if (!newSelectedFilters) {
          setAiSummary({ data: null, error: "", loading: false });
          return;
        }

        const {
          school_district,
          elemantary_school,
          high_school,
          middle_school,
        } = newSelectedFilters;
        const queryString = new URLSearchParams({
          sort: "listdate desc",
          view: "map",
        });

        const schoolFiltersIds = {
          high_school: "",
          middle_school: "",
          elemantary_school: "",
          school_district: "",
        };

        if (artifact?.filters?.schoolhigh && high_school === undefined) {
          schoolFiltersIds.high_school = String(artifact?.filters?.schoolhigh);
        }
        if (high_school) {
          schoolFiltersIds.high_school = String(high_school.value);
        }

        if (artifact?.filters?.schoolmiddle && middle_school === undefined) {
          schoolFiltersIds.middle_school = String(artifact?.filters?.schoolmiddle);
        }
        if (middle_school) {
          schoolFiltersIds.middle_school = String(middle_school.value);
        }

        if (artifact?.filters?.schoolelementary && elemantary_school === undefined) {
          schoolFiltersIds.elemantary_school = String(
            artifact?.filters?.schoolelementary
          );
        }
        if (elemantary_school) {
          schoolFiltersIds.elemantary_school = String(elemantary_school.value);
        }

        if (artifact?.filters?.school_district && school_district === undefined) {
          schoolFiltersIds.school_district = String(
            artifact?.filters?.school_district
          );
        }
        if (school_district) {
          schoolFiltersIds.school_district = String(school_district.value);
        }

        if (schoolFiltersIds.high_school) {
          queryString.append("schoolhigh", schoolFiltersIds.high_school);
        }
        if (schoolFiltersIds.middle_school) {
          queryString.append("schoolmiddle", schoolFiltersIds.middle_school);
        }
        if (schoolFiltersIds.elemantary_school) {
          queryString.append(
            "schoolelementary",
            schoolFiltersIds.elemantary_school
          );
        }
        if (schoolFiltersIds.school_district) {
          queryString.append(
            "school_district",
            schoolFiltersIds.school_district
          );
        }

        // nid with type="gm" is converted to "marketarea" name for the UI dropdown, but the
        // summary API needs the original numeric nid. Re-inject it here before serializing,
        // and drop marketarea so both filters aren't sent together (nid is authoritative).
        const filtersForSummary = { ...newSelectedFilters };
        if (artifact?.filters?.nid && filtersForSummary?.marketarea && !filtersForSummary?.nid) {
          queryString.append("nid", String(artifact.filters.nid));
          delete filtersForSummary.marketarea;
        }

        const completeQueryString = extractFilterValuesAndAppendInQueryString(queryString, filtersForSummary);
        const url = `${apiBaseUrl}/api/v1/chatbot/getsummary?${completeQueryString.toString()}`;
        const response = await (
          await fetch(url, { credentials: "include" })
        ).json();
        setAiSummary({
          data: response?.data?.results,
          error: "",
          loading: false,
        });
        trackEvent("AI Mode / Listing Search AI Summary");
      } catch (err: any) {
        captureException(err);
        setAiSummary({
          data: null,
          error: err?.message ?? "Failed to fetch AI summary",
          loading: false,
        });
        console.error(err);
      }
    };

    const onSendMessage = (filters: IFilters) => {
      onFiltersNewMessage?.(filters);
      setIsAiSummaryDrawerOpen(false);
      setIsFilterModalOpen(false);
    };

    const seeMore = (): string => {
      let url = artifact.response.url;
      let filterLink = "";

      if (url.includes("/listing?")) {
        url = url.replace("/listing?", "");
        filterLink = websiteHost + `/search/dosearch?${url}`;
      } else if (url.includes("/sold?")) {
        url = url.replace("/sold?", "");
        filterLink = websiteHost + `/search/soldsearch?${url}`;
      }

      return filterLink || url;
    };
    return (
      <div>
        <Swiper
          style={{ maxWidth: "100%" }}
          className="swiper-properties properties_slider s2 mt-2"
          pagination={{ clickable: true }}
          modules={[Navigation]}
          spaceBetween={10}
          slidesPerView={"auto"}     // ✅ important (fixed width slides)
          slidesPerGroup={1}         // ✅ next button moves 1 card
          navigation={true}
          grabCursor={true}
          simulateTouch={true}
          threshold={5}
          watchOverflow={true}
          breakpoints={{
            0: {
              slidesPerView: "auto",
              slidesPerGroup: 1,
              spaceBetween: 10,
            },
            768: {
              slidesPerView: "auto",
              slidesPerGroup: 1,
              spaceBetween: 12,
            },
            1067: {
              slidesPerView: 3,      // ✅ desktop shows 3 full cards
              slidesPerGroup: 1,     // keep 1 if you want step-by-step
              spaceBetween: 10,
            },
          }}
        >
          {propertiesCount > 0 &&
            properties.map((details, index) => (
              <SwiperSlide
                key={index}
                className={propertiesCount < 3 ? `swiper-slider-item` : ""}
              >
                <MlsSearchCard details={details} />
              </SwiperSlide>
            ))}
          {!hideLoadMore && propertiesCount > 0 && propertiesCount < totalNumberOfProperties && stop !== totalNumberOfProperties && !isProduction && !readOnly && (
            <SwiperSlide key='load-more' className={"swiper-slider-item"}>
              <div
                onClick={() => {
                  const updatedFilters = STATUSES_THAT_CLEAR_FOR.includes(filters.Status as string) ? { ...filters, For: "" } : filters;
                  setFilters(updatedFilters);

                  if (onFiltersNewMessage) {
                    const transformedFilters = toUIFilters(artifact.filters ?? {});
                    onFiltersNewMessage(transformedFilters, eSearchType.NextPage);
                  }
                  // onFiltersNewMessage?.(
                  //   { ...artifact?.filters, city: (artifact?.filters?.city as string)?.split(",") ?? [] } as any, // TODO: Fix this line
                  //   'next_page'
                  // )
                }}
                className="cardv2 cardv2--portrait mb-0 h-100 empty_card color_black cursor--pointer">
                <div>Load more properties</div>
              </div>
            </SwiperSlide>
          )}
        </Swiper>
        <div className={`filters-summary-and-results-container mt-1${isContentNotVisible ? " mb-4" : ""}`}>
          <div className="filters-summary-and-results-container">
            {hideFiltersAndAiSummary || isContentNotVisible ? null : (
              <div className="d-flex align-items-center">
                {artifact.filters && (
                  <button className="iconpill iconpill__icon filters_control opensearch_filters" title="Filters" aria-label="Filters" onClick={() => setIsFilterModalOpen(true)}>
                    <div className="iconpill__icon__img d-inline-block" style={{
                      position: "initial",
                      height: "initial"
                    }}>
                      <img src="https://content.harstatic.com/media/icons/filters_icon_for_search.svg" alt="Filters" />
                    </div>
                    <div className="iconpill__icon__text d-inline-block" style={{ padding: "9px 16px 9px 0px" }}>Filters</div></button>
                )}
                <button
                  onClick={() => {
                    setIsAiSummaryDrawerOpen(true);
                    getAiSummaryData();
                  }}
                  className="btn font-weight-bold btn--small d-flex align-items-center btn--less_padding ml-3"
                  style={{
                    backgroundColor: "rgb(237, 240, 250)",
                    border: "1px solid #d0d0d0",
                  }}
                >
                  <img
                    src="https://content.harstatic.com/media/temporary/har_ai_invert_logo.png"
                    className="mr-1"
                    alt="HAR AI"
                  />
                  Summary
                </button>
              </div>
            )}
            {!hideFiltersAndAiSummary && (
              <ResultsCountAndSeeMore
                seeMore={seeMore}
                start={artifact.response.start}
                stop={artifact.response.stop}
                total={artifact.response.total_number_of_properties}
              />
            )}
          </div>
          {message?.metadata?.search_listings && (
            <MessageActionBar
              messageId={message?.message_id}
              initialRating={message?.rating}
              content={message?.content}
              metadata={message?.metadata}
              className="ml-0"
            />
          )}

        </div>
        {
          isFilterModalOpen &&
          createPortal(
            <Filter
              setFilters={setFilters}
              filters={filters}
              closePopup={() => setIsFilterModalOpen(false)}
              send={onFiltersNewMessage}
            />,
            document.body
          )
        }
        {
          isAiSummaryDrawerOpen &&
          createPortal(
            <AiSummaryDrawer
              loading={aiSummary.loading}
              data={aiSummary.data}
              error={aiSummary.error}
              filters={filters}
              setFilters={setFilters}
              closeAiSummaryDrawer={() => setIsAiSummaryDrawerOpen(false)}
              onSendMessage={(updatedFilters) => {
                // When filters are updated in the drawer, use those updated filters
                onSendMessage(updatedFilters);
              }}
            />,
            document.body
          )
        }
      </div >
    );
  }
}
